import React, { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { isMobile } from "react-device-detect";
import { useEventContext } from "../../context/EventContext";
import { usePlayerContext } from "../../context/PlayerContext";
import { useLocaleContext } from "../../context/LocaleContext";
import { useModalContext } from "../../context/ModalContext";
import { useStreamContext } from "../../context/StreamContext";
import PartnersBaseView from "../../components/Partners/PartnersViews/PartnersBaseView";
import MemberDetailsModal from "../../components/Members/MembersComponents/MemberDetailsModal";
import MemberInfoCard from "../../components/Members/MembersComponents/MemberInfoCard";
import { CAttachedFile, CButton, CTags } from "../../components/UI";
import { TYPE_HYBRID } from "../../constants/common";
import "./style.scss";


const HallDescription = ({ block, page }) => {
    const { event } = useEventContext();
    const { currentEpisode: pCurrentEpisode } = usePlayerContext();
    const { stream: currentStream } = useStreamContext();
    const { renderModal } = useModalContext();
    const { get, getProperty } = useLocaleContext();

    const _currentEpisode = (currentStream?.episode || []).find(({ id }) => id === pCurrentEpisode?.id);

    const allowShowEpisode = !!_currentEpisode &&
        (!page || !page.participationType || !_currentEpisode.participationType ||
            [page.participationType, _currentEpisode.participationType].some(pType => pType === TYPE_HYBRID)
            || _currentEpisode.participationType === page.participationType
        );

    const currentEpisode = allowShowEpisode ? _currentEpisode : null

    const [isOpened, setOpened] = useState(!isMobile ? block?.showBlockExpanded ?? true : false);

    const episodeContentRef = useRef(null);
    const membersListRef = useRef([]);

    const visibleMembersGroups = useMemo(() => {
        return event.moduleEpisode.membersEnabled ? currentEpisode?.groups?.reduce((acc, group) => {
            const members = group.members
                .sort((a, b) => (a.important === b.important) ? 0 : a.important ? -1 : 1)
                .filter(({ member }) => !member.hide)
                .map((member) => {
                    return {
                        ...member,
                        visible: true,
                    };
                });
            if (members.length > 0) {
                acc.push({ ...group, members });
            }
            return acc;
        }, []) : [];
    }, [currentEpisode]);

    const showBlock = useMemo(() => {
        if (!currentEpisode) return false;
        return !!getProperty(currentEpisode, 'name')
            || !!getProperty(currentEpisode, 'info')
            || !!currentEpisode.tags?.length
            || !!currentEpisode.partners?.length
            || !!currentEpisode.groups?.length
            || !!currentEpisode.files?.length
            || block?.showInStreams?.includes(currentStream?.id)
    }, [currentEpisode]);

    const episodeInfoConfig = useMemo(() => {
        return {
            visibility: 'visible',
            display: isOpened ? 'block' : 'none',
            opacity: isOpened ? 1 : 0,
            transform: `translateY(${isOpened ? 0 : -15}px)`,
            transitionEnd: {
                visibility: isOpened ? 'visible' : 'hidden',
                display: isOpened ? 'block' : 'none',
            }
        };
    }, [isOpened]);

    const episodeInfoTransition = {
        ease: 'linear',
        duration: .3,
    };

    function handleToggleInfo(e) {
        e.stopPropagation();
        setOpened(!isOpened);
    }

    if (!currentEpisode || !showBlock) {
        return null;
    }

    return (
        <section
            ref={episodeContentRef}
            className={"page-block hall-description"}
        >
            <h1>{getProperty(currentEpisode, 'name')}</h1>
            {
                !!currentEpisode.tags?.length &&
                <CTags
                    tags={currentEpisode.tags}
                    size="small"
                />
            }
            {
                !!currentEpisode.partners?.length &&
                <PartnersBaseView
                    partners={currentEpisode.partners}
                    size="base"
                />
            }
            {
                (!!currentEpisode.groups?.length || !!getProperty(currentEpisode, 'info') || !!currentEpisode.files?.length) &&
                <CButton
                    blank
                    label={isOpened ? get('messages.curtail') : get('messages.expand')}
                    icon="arrow_down"
                    iconPosition="right"
                    className={`hall-description__round-button ${isOpened ? 'hall-description__round-button--opened' : ''}`}
                    onClick={(e) => handleToggleInfo(e)}
                />
            }
            <motion.div
                key="HallInfoSimpleView"
                animate={episodeInfoConfig}
                initial={episodeInfoConfig}
                transition={episodeInfoTransition}
            >
                {
                    !!getProperty(currentEpisode, 'info') &&
                    <p className="hall-description__info">
                        {getProperty(currentEpisode, 'info')}
                    </p>
                }
                {
                    !!visibleMembersGroups?.length &&
                    visibleMembersGroups.map((group, index) => (
                        <div
                            ref={(item) => membersListRef.current[index] = item}
                            key={group?.id}
                            className="hall-description__members"
                        >
                            <h3 className="hall-description__members-title">
                                {getProperty(group?.role, group?.members?.length > 1 ? 'groupTitle' : 'title')}
                            </h3>
                            <motion.div
                                key={`HallInfoSimpleView-${group?.id}`}
                                animate={episodeInfoConfig}
                                initial={episodeInfoConfig}
                                transition={episodeInfoTransition}
                            >
                                <div className="hall-description__members-list">
                                    {
                                        group?.members?.map((episodeMember) => (
                                            episodeMember?.visible &&
                                            <div
                                                key={episodeMember?.id}
                                                className="hall-description__members-card"
                                            >
                                                <MemberInfoCard
                                                    episodeMember={episodeMember}
                                                    episode={currentEpisode}
                                                    group={group}
                                                    size="lg"
                                                    onMemberClick={(member) => {
                                                        renderModal(MemberDetailsModal, { lockPageScroll: false, event, member });
                                                    }}
                                                />
                                            </div>
                                        ))
                                    }
                                </div>
                            </motion.div>
                            {/* {
                                (group?.members?.length > membersOnRow) &&
                                <CButton
                                    blank
                                    label={get('label.show-more')}
                                    icon="arrow_down"
                                    iconPosition="right"
                                    className={`hall-description__round-button hall-description__show-more-button hall-description__show-more-button-${group.id}`}
                                    onClick={() => handleChangeMembersVisibility(group?.id, index)}
                                />
                            } */}
                        </div>
                    ))
                }
                {
                    !!currentEpisode.files?.length &&
                    <div className="schedule-details-modal-materials">
                        <div className="schedule-details-modal-materials__title">
                            {get('title.materials.attached')}
                        </div>
                        <div className="schedule-details-modal-materials__list">
                            {
                                currentEpisode.files.map((file) => (
                                    <CAttachedFile
                                        key={file?.id}
                                        file={file}
                                    />
                                ))
                            }
                        </div>
                    </div>
                }
            </motion.div>
        </section>
    );
};

export default HallDescription;

// const HallDescription = ({ block, page }) => {
//     const { event } = useEventContext();
//     const { currentEpisode: playerCurrentEpisode } = usePlayerContext();
//     const { stream: currentStream } = useStreamContext();
//     const { renderModal } = useModalContext();
//     const { get, getProperty } = useLocaleContext();

//     const [isOpened, setOpened] = useState(!isMobile ? block?.showBlockExpanded ?? true : false);

//     const episodeContentRef = useRef(null);
//     const membersListRef = useRef([]);

//     const allowShowEpisode = !!playerCurrentEpisode && 
//         (!page?.participationType  ||  page?.participationType === TYPE_HYBRID) ||
//         (!playerCurrentEpisode?.participationType || 
//         playerCurrentEpisode?.participationType === page?.participationType)

//     const currentEpisode = allowShowEpisode ? playerCurrentEpisode : null

//     const visibleMembersGroups = useMemo(() => {
//         return event.moduleEpisode.membersEnabled ? currentEpisode?.groups?.reduce((acc, group) => {
//             const members = group.members
//                 .sort((a, b) => (a.important === b.important)? 0 : a.important? -1 : 1)
//                 .filter(({ member }) => !member.hide)
//                 .map((member) => {
//                     return {
//                         ...member,
//                         visible: true,
//                     };
//             });
//             if (members.length > 0) {
//                 acc.push({...group, members});
//             }
//             return acc;
//         }, []) : [];
//     }, [currentEpisode]);

//     const showBlock = useMemo(() => {
//         if (!currentEpisode) return false;
//         return !!getProperty(currentEpisode, 'name')
//             || !!getProperty(currentEpisode, 'info')
//             || !!currentEpisode.tags?.length
//             || !!currentEpisode.partners?.length
//             || !!currentEpisode.groups?.length
//             || !!currentEpisode.files?.length
//             || block?.showInStreams?.includes(currentStream?.id)
//     }, [currentEpisode]);

//     const episodeInfoConfig = useMemo(() => {
//         return {
//             visibility: 'visible',
//             display: isOpened ? 'block' : 'none',
//             opacity: isOpened ? 1 : 0,
//             transform: `translateY(${isOpened ? 0 : -15}px)`,
//             transitionEnd: {
//                 visibility: isOpened ? 'visible' : 'hidden',
//                 display: isOpened ? 'block' : 'none',
//             }
//         };
//     }, [isOpened]);

//     const episodeInfoTransition = {
//         ease: 'linear',
//         duration: .3,
//     };

//     function handleToggleInfo(e) {
//         e.stopPropagation();
//         setOpened(!isOpened);
//     }

//     if (!currentEpisode || !showBlock) {
//         return null;
//     }

//     return (
//         <section
//             ref={episodeContentRef}
//             className={"page-block hall-description"}
//         >
//             <h1>{getProperty(currentEpisode, 'name')}</h1>
//             {
//                 !!currentEpisode.tags?.length &&
//                 <CTags
//                     tags={currentEpisode.tags}
//                     size="small"
//                 />
//             }
//             {
//                 !!currentEpisode.partners?.length &&
//                 <PartnersBaseView
//                     partners={currentEpisode.partners}
//                     size="base"
//                 />
//             }
//             {
//                 (!!currentEpisode.groups?.length || !!getProperty(currentEpisode, 'info') || !!currentEpisode.files?.length) &&
//                 <CButton
//                     blank
//                     label={isOpened ? get('messages.curtail') : get('messages.expand')}
//                     icon="arrow_down"
//                     iconPosition="right"
//                     className={`hall-description__round-button ${isOpened ? 'hall-description__round-button--opened' : ''}`}
//                     onClick={(e) => handleToggleInfo(e)}
//                 />
//             }
//             <motion.div
//                 key="HallInfoSimpleView"
//                 animate={episodeInfoConfig}
//                 initial={episodeInfoConfig}
//                 transition={episodeInfoTransition}
//             >
//                 {
//                     !!getProperty(currentEpisode, 'info') &&
//                     <p className="hall-description__info">
//                         {getProperty(currentEpisode, 'info')}
//                     </p>
//                 }
//                 {
//                     !!visibleMembersGroups?.length &&
//                     visibleMembersGroups.map((group, index) => (
//                         <div
//                             ref={(item) => membersListRef.current[index] = item}
//                             key={group?.id}
//                             className="hall-description__members"
//                         >
//                             <h3 className="hall-description__members-title">
//                                 {getProperty(group?.role, group?.members?.length > 1 ? 'groupTitle' : 'title')}
//                             </h3>
//                             <motion.div
//                                 key={`HallInfoSimpleView-${group?.id}`}
//                                 animate={episodeInfoConfig}
//                                 initial={episodeInfoConfig}
//                                 transition={episodeInfoTransition}
//                             >
//                                 <div className="hall-description__members-list">
//                                     {
//                                         group?.members?.map((episodeMember) => (
//                                             episodeMember?.visible &&
//                                             <div
//                                                 key={episodeMember?.id}
//                                                 className="hall-description__members-card"
//                                             >
//                                                 <MemberInfoCard
//                                                     episodeMember={episodeMember}
//                                                     episode={currentEpisode}
//                                                     group={group}
//                                                     size="lg"
//                                                     onMemberClick={(member) => {
//                                                         renderModal(MemberDetailsModal, { lockPageScroll: false, event, member});
//                                                     }}
//                                                 />
//                                             </div>
//                                         ))
//                                     }
//                                 </div>
//                             </motion.div>
//                             {/* {
//                                 (group?.members?.length > membersOnRow) &&
//                                 <CButton
//                                     blank
//                                     label={get('label.show-more')}
//                                     icon="arrow_down"
//                                     iconPosition="right"
//                                     className={`hall-description__round-button hall-description__show-more-button hall-description__show-more-button-${group.id}`}
//                                     onClick={() => handleChangeMembersVisibility(group?.id, index)}
//                                 />
//                             } */}
//                         </div>
//                     ))
//                 }
//                 {
//                     !!currentEpisode.files?.length &&
//                     <div className="schedule-details-modal-materials">
//                         <div className="schedule-details-modal-materials__title">
//                             {get('title.materials.attached')}
//                         </div>
//                         <div className="schedule-details-modal-materials__list">
//                             {
//                                 currentEpisode.files.map((file) => (
//                                     <CAttachedFile
//                                         key={file?.id}
//                                         file={file}
//                                     />
//                                 ))
//                             }
//                         </div>
//                     </div>
//                 }
//             </motion.div>
//         </section>
//     );
// };

// export default HallDescription;

